import { useEffect, useRef } from "react";
import { useAuthenticationProvider } from "@/hooks/authentication/useAuthenticationProvider";
import { useAccount } from "wagmi";

/**
 * Hook to detect when user switches wallet accounts externally (e.g., in Rabby).
 * When wagmi detects an address change while authenticated, this forces a logout
 * to ensure the hub user state stays in sync with the actual connected wallet.
 */
export const useDetectWalletAddressChange = () => {
  const { address } = useAccount();
  const { authenticated, logout, identityProvider } = useAuthenticationProvider();
  const previousAddressRef = useRef<string | undefined>(undefined);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    // Skip if using local auth (dev mode) or TON - only applies to Privy/wagmi
    if (identityProvider !== "privy") {
      return;
    }

    // Skip first render to allow initial connection
    if (!isInitializedRef.current) {
      previousAddressRef.current = address;
      isInitializedRef.current = true;
      return;
    }

    // Detect address change while authenticated
    const previousAddress = previousAddressRef.current;
    const addressChanged = previousAddress && address && previousAddress !== address;

    if (authenticated && addressChanged) {
      logout();
    }

    previousAddressRef.current = address;
  }, [address, authenticated, logout, identityProvider]);
};
